import { set } from 'lodash/fp';
import { useCallback } from 'react';
import { TSuggestedComponentTypes } from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.interface';
import { useModal } from 'src/hooks';
import { useGlobalComponentsScreen } from 'src/hooks/useGlobalComponentsScreen';
import {
  DyProductSource,
  IDisclaimerPosition,
  ILayout,
  IScreen,
  ISection,
  SectionTypes,
} from 'src/interfaces';
import { parseLayoutConfig } from 'src/utils';

import { useGetScreenGridsInfo } from '../../../hooks/useGetScreenGridsInfo';
import { ILayoutScreenBuilder } from '../LayoutScreenBuilder';

export const useLayoutScreenBuilder = (props: ILayoutScreenBuilder) => {
  const globalComponentsScreen = useGlobalComponentsScreen(props.layout);
  const {
    screenIndex,
    scale,
    formMethods,
    layerIndex,
    currentPositionGridLines,
    setLayout,
    onUpdateScreenPredefinedSections,
    onUpdatePositionsGridLines,
  } = props;
  const screenConfigModal = useModal();
  const {
    orientation,
    rows,
    cols,
    disclaimerPosition,
    disclaimerType,
    gridSectionHeight,
    gridSectionWidth,
    disclaimerHeightScaled,
    disclaimerWidth,
    disclaimerHeightOriginal,
    screenHeight,
  } = useGetScreenGridsInfo(currentPositionGridLines, screenIndex, scale);

  const handleOpenScreenConfigModal = useCallback(() => {
    formMethods.setValue('orientation', orientation);
    formMethods.setValue('disclaimer', !!disclaimerPosition);
    formMethods.setValue('screen_grid', `${cols} X ${rows}`);
    formMethods.setValue('size', disclaimerType);
    formMethods.setValue('placement', disclaimerPosition);

    screenConfigModal.onOpenModal();
  }, [
    cols,
    disclaimerPosition,
    disclaimerType,
    formMethods,
    orientation,
    rows,
    screenConfigModal,
  ]);
  const getNewUpdatedDYRSection = (
    screenConfigString: string,
    section: ISection,
  ) => {
    const { disclaimerPosition: currentDisclaimerPosition } =
      parseLayoutConfig(screenConfigString);
    let topOffset = section.top_offset;

    if (
      currentDisclaimerPosition === IDisclaimerPosition.TOP &&
      disclaimerPosition === IDisclaimerPosition.BOTTOM
    ) {
      topOffset = screenHeight! - section.height;
    } else if (
      currentDisclaimerPosition === IDisclaimerPosition.BOTTOM &&
      disclaimerPosition === IDisclaimerPosition.TOP
    ) {
      topOffset = screenHeight! - section.height - disclaimerHeightOriginal!;
    }

    return {
      ...section,
      top_offset: topOffset,
    };
  };
  const updateLayoutScreenSections = (
    layout: ILayout,
    screenConfigString: string,
  ) => {
    if (
      !layout.config?.sectionTypes?.includes(SectionTypes.DYR) ||
      screenIndex !== 2
    ) {
      return layout.screens;
    }

    const newScreens: IScreen[] = [];

    layout.screens.forEach((screen, index) => {
      if (index !== 2) {
        newScreens.push(screen);
      } else {
        newScreens.push({
          ...screen,
          sections: screen.sections.map((section) => {
            if (
              (
                section.config
                  ?.suggestedComponentTypes as TSuggestedComponentTypes
              )?.includes(DyProductSource.DYR)
            ) {
              return getNewUpdatedDYRSection(screenConfigString, section);
            }

            return section;
          }),
        });
      }
    });

    return newScreens;
  };
  const onSaveScreenConfigModal = (screenConfigString: string) => {
    const newGridLines = set(
      [screenIndex],
      screenConfigString,
      currentPositionGridLines,
    );

    setLayout((prev) => {
      const newLayout: ILayout = {
        ...(prev as ILayout),
        screens: updateLayoutScreenSections(prev!, screenConfigString),
      };

      onUpdateScreenPredefinedSections(screenConfigString);
      onUpdatePositionsGridLines({
        position: layerIndex,
        gridLines: newGridLines,
      });

      return newLayout;
    });
  };

  return {
    rows,
    cols,
    disclaimerPosition,
    disclaimerType,
    gridSectionHeight,
    gridSectionWidth,
    disclaimerHeightScaled,
    disclaimerWidth,
    screenConfigModal,
    handleOpenScreenConfigModal,
    onSaveScreenConfigModal,
    globalComponentsScreen,
  };
};
